import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'

import { api } from '../../api/client'
import type { PatchFleet, PatchFleetHost, PatchMissingItem } from '../../api/client'
import { useAsync } from '../../hooks/useAsync'
import { t } from '../../i18n/lingua'
import { BottoniEsporta } from '../BottoniEsporta'
import { DataTable } from '../DataTable'
import { Card, EmptyState, ErrorBanner, Spinner, formatDateTime } from '../common'
import {
  KevChip, ReadinessChips, SeverityChip, chipClass, platformLabel, reasonHint, reasonLabel,
} from './labels'
import type { WizardPrefill } from './NewJobWizard'

export type MachineFilter = 'all' | 'missing' | 'overdue' | 'not_ready' | 'up_to_date'

/**
 * Every patchable machine: what it misses, how late, whether it can be
 * patched now and why not. Select machines to start a job on them.
 */
export function Machines({ data, initialFilter, canWrite, onNewJob }: {
  data: PatchFleet
  initialFilter?: MachineFilter
  canWrite: boolean
  onNewJob: (prefill?: WizardPrefill) => void
}) {
  const [filter, setFilter] = useState<MachineFilter>(initialFilter ?? 'all')
  const [env, setEnv] = useState('')
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const envs = useMemo(() => [...new Set(data.hosts.map((h) => h.environment))].sort(), [data.hosts])
  const rows = data.hosts.filter((h) => {
    if (env && h.environment !== (env === '-' ? '' : env)) return false
    switch (filter) {
      case 'missing': return !h.up_to_date
      case 'overdue': return h.overdue > 0 || h.end_of_life
      case 'not_ready': return h.blocked || h.reasons.includes('agent_offline') || h.reasons.includes('agent_update')
      case 'up_to_date': return h.up_to_date
      default: return true
    }
  })
  const count = (f: MachineFilter) => data.hosts.filter((h) => {
    switch (f) {
      case 'missing': return !h.up_to_date
      case 'overdue': return h.overdue > 0 || h.end_of_life
      case 'not_ready': return h.blocked || h.reasons.includes('agent_offline') || h.reasons.includes('agent_update')
      case 'up_to_date': return h.up_to_date
      default: return true
    }
  }).length

  return (
    <Card title={t('Macchine')}
      actions={<BottoniEsporta path={(fmt) => `/patching/export?what=hosts&fmt=${fmt}`} nome="censo-patch-macchine" disabled={!data.hosts.length} />}>
      <div className="patch-toolbar">
        <div className="segmented" role="group" aria-label={t('Filtro')}>
          {([
            ['all', t('Tutte')], ['missing', t('Da aggiornare')], ['overdue', t('Fuori SLA')],
            ['not_ready', t('Non pronte')], ['up_to_date', t('Aggiornate')],
          ] as Array<[MachineFilter, string]>).map(([k, label]) => (
            <button key={k} type="button" className={filter === k ? 'segmented__on' : ''}
              onClick={() => setFilter(k)}>{label} ({count(k)})</button>
          ))}
        </div>
        {envs.length > 1 && (
          <select value={env} onChange={(e) => setEnv(e.target.value)} aria-label={t('Ambiente')}>
            <option value="">{t('Tutti gli ambienti')}</option>
            {envs.map((e) => <option key={e || '-'} value={e || '-'}>{e || t('senza «{k}»', { k: data.environment_key })}</option>)}
          </select>
        )}
      </div>
      {canWrite && selected.size > 0 && (
        <div className="patch-selbar">
          <strong>{t('{n} macchine scelte', { n: selected.size })}</strong>
          <button onClick={() => onNewJob({ ciIds: [...selected] })}>{t('Nuovo intervento su queste')}</button>
          <button className="secondary" onClick={() => setSelected(new Set())}>{t('Deseleziona')}</button>
        </div>
      )}
      {rows.length === 0 ? (
        <EmptyState title={t('Nessuna macchina con questo filtro.')} />
      ) : (
        <DataTable
          rows={rows}
          rowKey={(r) => r.ci_id}
          conteggio={{ nome: t('macchine') }}
          paginazione={{ predefinita: 25 }}
          selezione={canWrite ? { chiavi: selected, onCambia: setSelected, id: (r) => r.ci_id } : undefined}
          expand={(r) => <MachinePatches host={r} canWrite={canWrite} onNewJob={onNewJob} />}
          columns={[
            {
              key: 'name', header: t('Macchina'), value: (r) => r.name,
              render: (r) => (
                <>
                  <Link to={`/cis/${r.ci_id}`} className="patch-name">{r.name}</Link>
                  <div className="text-muted patch-sub">{r.os || platformLabel(r.platform)}</div>
                </>
              ),
            },
            { key: 'platform', header: t('Sistema'), value: (r) => platformLabel(r.platform), filter: 'select' },
            { key: 'environment', header: t('Ambiente'), value: (r) => r.environment, filter: 'select' },
            {
              key: 'missing', header: t('Patch mancanti'), value: (r) => r.missing + r.software,
              render: (r) => <MissingCell h={r} />,
            },
            {
              key: 'overdue', header: t('Fuori SLA'), value: (r) => r.overdue,
              render: (r) => (r.overdue
                ? <><span className={chipClass('warn')}>{r.overdue}</span><div className="text-muted patch-sub">{t('la più vecchia da {n} giorni', { n: r.oldest_days })}</div></>
                : r.oldest_days ? <span className="text-muted patch-sub">{t('la più vecchia da {n} giorni', { n: r.oldest_days })}</span> : ''),
            },
            {
              key: 'ready', header: t('Aggiornabile'), value: (r) => r.reasons.map((x) => reasonLabel(x)).join(' ') || t('Pronta'),
              render: (r) => <ReadinessChips reasons={r.reasons} missingTags={r.missing_tags} />,
            },
            {
              key: 'last', header: t('Ultima installazione'), value: (r) => r.last_patch_at ?? '',
              render: (r) => (r.last_patch_at ? formatDateTime(r.last_patch_at) : <span className="text-muted">{t('mai')}</span>),
            },
          ]}
        />
      )}
    </Card>
  )
}

function MissingCell({ h }: { h: PatchFleetHost }) {
  if (h.end_of_life) return <span className={chipClass('error')}>{t('Fuori supporto')}</span>
  if (h.up_to_date) return <span className={chipClass('ok')}>{t('Aggiornata')}</span>
  const parts = []
  if (h.kb) parts.push(t('{n} KB', { n: h.kb }))
  if (h.packages) parts.push(t('{n} pacchetti', { n: h.packages }))
  if (h.software) parts.push(t('{n} software', { n: h.software }))
  return (
    <>
      <strong>{h.missing + h.software}</strong> <SeverityChip severity={h.worst_severity} />
      {h.exploited > 0 && <KevChip />}
      <div className="text-muted patch-sub">
        {parts.join(' · ')}{h.critical ? ` · ${t('{n} critiche', { n: h.critical })}` : ''}
      </div>
    </>
  )
}

export function missingKey(it: PatchMissingItem): string | null {
  if (it.key) return it.key
  if (it.kind === 'kb' && it.kb) {
    const k = it.kb.toUpperCase()
    return `kb:${k.startsWith('KB') ? k : 'KB' + k}`
  }
  if (it.kind === 'packages' && it.package) return `pkg:${it.package}`
  return null
}

/**
 * The patches one machine misses, with what can be installed from here: all
 * the security updates, or the ones ticked. Used in the machine list and on
 * the machine's own page.
 */
export function MachinePatches({ host, ciId, canWrite, onNewJob }: {
  host?: PatchFleetHost
  ciId?: string
  canWrite: boolean
  onNewJob: (prefill?: WizardPrefill) => void
}) {
  const id = host?.ci_id ?? ciId ?? ''
  const missing = useAsync(() => api.patchMissing(id), [id])
  const [ticked, setTicked] = useState<Set<string>>(new Set())
  if (missing.loading && !missing.data) return <Spinner />
  if (missing.error) return <ErrorBanner error={missing.error} />
  const items = missing.data?.items ?? []
  const system = items.filter((it) => it.kind === 'packages' || it.kind === 'kb')
  const software = items.filter((it) => it.kind === 'software')
  const container = items.filter((it) => it.kind === 'container')
  const blocking = (host?.reasons ?? []).filter((r) => ['no_agent', 'os_unknown', 'missing_tags'].includes(r))

  return (
    <div className="patch-machine">
      {host && host.reasons.length > 0 && (
        <ul className="patch-reasons">
          {host.reasons.map((r) => (
            <li key={r}><strong>{reasonLabel(r, host.missing_tags)}</strong> — {reasonHint(r)}</li>
          ))}
        </ul>
      )}
      {system.length === 0 && software.length === 0 && container.length === 0 && (
        <p className="text-muted">{t('Nessuna patch mancante trovata su questa macchina.')}</p>
      )}
      {system.length > 0 && (
        <>
          <h4 className="patch-machine__title">{t('Patch di sistema mancanti ({n})', { n: system.length })}</h4>
          <ul className="patch-list">
            {system.map((it, i) => {
              const key = missingKey(it)
              return (
                <li key={key ?? `i${i}`}>
                  {canWrite && key && (
                    <input type="checkbox" aria-label={t('Scegli')} checked={ticked.has(key)} onChange={(e) => {
                      const n = new Set(ticked)
                      if (e.target.checked) n.add(key)
                      else n.delete(key)
                      setTicked(n)
                    }} />
                  )}
                  <span className="mono">{it.kind === 'kb' ? it.kb : it.package}</span>
                  <SeverityChip severity={it.severity} />
                  {it.exploited && <KevChip />}
                  <span className="text-muted">{it.kind === 'kb' ? it.product : it.ecosystem}</span>
                </li>
              )
            })}
          </ul>
        </>
      )}
      {software.length > 0 && (
        <>
          <h4 className="patch-machine__title">{t('Software installato da aggiornare ({n})', { n: software.length })}</h4>
          <p className="field__hint">
            {t('Programmi con vulnerabilità note (browser, runtime, strumenti): su Windows si aggiornano con winget o Chocolatey della macchina, o con l’aggiornatore del produttore (Chrome, Edge); su Linux dal loro repository.')}
          </p>
          <ul className="patch-list">
            {software.map((it, i) => (
              <li key={it.key ?? i}>
                {canWrite && it.key && (
                  <input type="checkbox" aria-label={t('Scegli')} checked={ticked.has(it.key)} onChange={(e) => {
                    const n = new Set(ticked)
                    if (e.target.checked) n.add(it.key!)
                    else n.delete(it.key!)
                    setTicked(n)
                  }} />
                )}
                <span className="mono">{it.product}</span>
                {it.version && <span className="text-muted">{it.version}</span>}
                <SeverityChip severity={it.severity} />
                {it.exploited && <KevChip />}
              </li>
            ))}
          </ul>
        </>
      )}
      {container.length > 0 && (
        <>
          <h4 className="patch-machine__title">{t('Pacchetti nei container ({n})', { n: container.length })}</h4>
          <p className="field__hint">
            {t('Pacchetti vulnerabili dentro le immagini dei container che girano su questa macchina: il gestore dei pacchetti della macchina non li tocca. Si correggono ricostruendo l’immagine da una base aggiornata e ridistribuendo i container.')}
          </p>
          <ul className="patch-list">
            {container.map((it, i) => (
              <li key={`${it.package}|${it.ecosystem}|${i}`}>
                <span className="mono">{it.package}</span>
                {it.version && <span className="text-muted">{it.version}</span>}
                <SeverityChip severity={it.severity} />
                {it.exploited && <KevChip />}
                <span className="text-muted">
                  {t('nel container')} {it.images?.length ? it.images.join(', ') : it.ecosystem}
                  {it.fixed ? ` · ${t('corretto in {v}', { v: it.fixed })}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </>
      )}
      {canWrite && (system.length > 0 || software.length > 0) && (
        <div className="rowactions patch-machine__actions">
          {system.length > 0 && (
            <button disabled={blocking.length > 0} title={blocking.map((r) => reasonLabel(r)).join(', ')}
              onClick={() => onNewJob({ kind: 'security_all', ciIds: [id] })}>
              {t('Installa tutte le patch di sicurezza')}
            </button>
          )}
          {software.length > 0 && missing.data?.platform === 'windows' && (
            <button className="secondary" disabled={blocking.length > 0}
              onClick={() => onNewJob({ kind: 'third_party', ciIds: [id] })}>
              {t('Aggiorna tutti i programmi')}
            </button>
          )}
          <button className="secondary" disabled={!ticked.size || blocking.length > 0}
            onClick={() => onNewJob({ kind: 'selected', ciIds: [id], patchKeys: [...ticked] })}>
            {ticked.size ? t('Installa le {n} scelte', { n: ticked.size }) : t('Installa le scelte')}
          </button>
        </div>
      )}
    </div>
  )
}
